import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Facebook, Clock, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import { useContent } from "@/context/ContentContext";

export default function Contact() {
  const { content } = useContent();
  const contact = content?.contact || {};
  const hours = content?.hours || [];
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const parts = [`Hi Keys Event Center! I'm ${form.name}.`, form.message];
    const reply = [form.email, form.phone].filter(Boolean).join(", ");
    if (reply) parts.push(`(Reply to: ${reply})`);
    window.location.href = `sms:19189313122?&body=${encodeURIComponent(parts.filter(Boolean).join(" "))}`;
  };

  const field = "w-full bg-[#1a140e] border border-[rgba(211,218,221,0.16)] rounded-xl px-4 py-3 text-[#f6ecd4] placeholder:text-[#7f9a9a] outline-none focus:border-[#2fd0c6] focus:bg-[#241a11] focus:shadow-[0_0_0_3px_rgba(47,208,198,0.18)] transition";

  return (
    <div className="pt-32 pb-24">
      <Seo title="Contact | Keys Event Center" description="Get in touch with Keys Event Center in Keys / Park Hill, Oklahoma. Call, email, or send us a message to book your private event." />
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading eyebrow="Let's Talk" title="Get In Touch" subtitle="Questions about booking or a big event? We'd love to hear from you." />
        <div className="grid lg:grid-cols-2 gap-10">
          <motion.form initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} onSubmit={submit}
            className="card p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required aria-label="Your name" autoComplete="name" placeholder="Your Name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
              <input required type="email" aria-label="Email" autoComplete="email" placeholder="Email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </div>
            <input type="tel" aria-label="Phone (optional)" autoComplete="tel" placeholder="Phone (optional)" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
            <textarea required rows={5} aria-label="Message" placeholder="How can we help?" className={field} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
            <button type="submit" disabled={loading} className="btn-primary w-full py-4 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit">
              {loading ? "Sending..." : <><Send size={18} /> Text Us</>}
            </button>
          </motion.form>

          <div className="space-y-6">
            <div className="card p-8 space-y-4">
              <a href={`tel:${contact.phone}`} className="flex items-center gap-4 text-[#cfe3e1] hover:text-[#ff5147] transition-colors" data-testid="contact-phone-link">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-[#241a11] text-[#2fd0c6]"><Phone size={20} /></span>
                <div><p className="text-xs text-[#8fa5a5] uppercase tracking-wide">Call Us</p><p className="font-semibold text-[#f6ecd4]">{contact.phone}</p></div>
              </a>
              <a href={`mailto:${contact.email}`} className="flex items-center gap-4 text-[#cfe3e1] hover:text-[#ff5147] transition-colors">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-[#2a1414] text-[#ff5147]"><Mail size={20} /></span>
                <div><p className="text-xs text-[#8fa5a5] uppercase tracking-wide">Email</p><p className="font-semibold text-[#f6ecd4]">{contact.email}</p></div>
              </a>
              <div className="flex items-center gap-4 text-[#cfe3e1]">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-[#2a2410] text-[#f6b64a]"><MapPin size={20} /></span>
                <div><p className="text-xs text-[#8fa5a5] uppercase tracking-wide">Location</p><p className="font-semibold text-[#f6ecd4]">{contact.address}</p></div>
              </div>
              <a href={contact.facebook} target="_blank" rel="noreferrer" aria-label="Follow us on Facebook (opens in a new tab)" className="flex items-center gap-4 text-[#cfe3e1] hover:text-[#ff5147] transition-colors">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-[#241a11] text-[#2fd0c6]"><Facebook size={20} /></span>
                <div><p className="text-xs text-[#8fa5a5] uppercase tracking-wide">Follow</p><p className="font-semibold text-[#f6ecd4]">Facebook</p></div>
              </a>
            </div>

            <div className="card p-8">
              <div className="flex items-center gap-2 mb-4"><Clock size={18} className="text-[#2fd0c6]" /><h3 className="font-display font-bold text-lg text-[#f6ecd4]">Hours</h3></div>
              <ul className="space-y-2 text-sm">
                {hours.map((h) => (<li key={h.day} className="flex justify-between text-[#a9bdbd]"><span>{h.day}</span><span className="font-medium text-[#f6ecd4]">{h.hours}</span></li>))}
              </ul>
            </div>

            <div className="rounded-2xl overflow-hidden border border-[rgba(211,218,221,0.14)] h-64">
              <iframe title="Keys Event Center Map" src={contact.map_embed} width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
